/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react'
import { Box, Rating, Typography } from '@mui/material'
import { DataGrid, GridColumns } from '@mui/x-data-grid'
import { darken, lighten, styled } from '@mui/material/styles'
import { PaletteMode } from '@mui/material'
import { SiteData, DpeFullLabel } from '../types'
import { CustomProgressBar } from './users.component'
import sumBy from 'lodash/sumBy'
import DpeLabel from '@/views/newdashboard/components/dpeLabel.component'

const getBackgroundColor = (color: string, mode: PaletteMode) =>
    mode === 'dark' ? darken(color, 0.7) : lighten(color, 0.7)

const getHoverBackgroundColor = (color: string, mode: PaletteMode) =>
    mode === 'dark' ? darken(color, 0.6) : lighten(color, 0.6)

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    '& .MuiDataGrid-row': {
        backgroundColor: 'white',
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: theme.palette.background.paper,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 'bold',
    },
    '& .super-app-theme--average': {
        backgroundColor: getBackgroundColor('#ffe082', theme.palette.mode),
        '&:hover': {
            backgroundColor: getHoverBackgroundColor('#ffe082', theme.palette.mode),
        },
    },
}))

const getSeriesSum = (series: SiteData['series'], type: string, subType: string | null = null): number => {
    const s = series.find((s) => s.type === type && s.subType === subType)
    if (!s) return 0
    return sumBy(s.data, 'value')
}

const consumptionDataFormatter = (value: number, unit: string) =>
    `${Intl.NumberFormat('fr-FR').format(Math.round(value))} ${unit}`

const addMetrics = (site: SiteData) => {
    const totalConsumptionFinal = getSeriesSum(site.series, 'energy_total', 'final')
    const totalConsumptionPrimary = getSeriesSum(site.series, 'energy_total', 'primary')
    const totalCO2 = getSeriesSum(site.series, 'energy_total', 'co2')

    return {
        ...site,
        totalConsumptionFinal,
        totalConsumptionPrimary,
        totalCO2,
    }
}

const DpeLabelWithValue: FC<{ dpe: DpeFullLabel; type: string }> = ({ dpe, type }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DpeLabel
            type={type === 'emission' ? 'greenhouse' : 'consumption'}
            size="small"
            width={60}
            dpeClass={dpe.label}
        />
        <Typography sx={{ fontSize: 'inherit' }}>{consumptionDataFormatter(dpe.value, dpe.unit)}</Typography>
    </Box>
)

const columns: GridColumns = [
    {
        field: 'name',
        headerName: 'Nom',
        width: 200,
        renderCell: (params) => {
            return <strong>{params?.value}</strong>
        },
    },
    {
        field: 'dpe.consumption.value',
        headerName: 'DPE',
        width: 250,
        valueGetter: (params) => params.row.dpe.consumption.value,
        renderCell: (params) => {
            const dpeLabel = params.row.dpe.consumption
            return <DpeLabelWithValue dpe={dpeLabel} type="consumption" />
        },
    },

    { field: 'brand', headerName: 'Groupe', width: 200 },
    { field: 'subBrand', headerName: 'Marque', width: 200 },

    // { field: 'consoBrute', headerName: 'Consommation brute (kWh)', type: 'number', width: 220 },
    // { field: 'consoSource', headerName: 'Consommation par source (kWh)', type: 'number', width: 240 },
    // { field: 'consoTempCorr', headerName: 'Consommation DJU (kWh)', type: 'number', width: 220 },
    // { field: 'consoM2', headerName: 'Consommation par m² (kWh)', type: 'number', width: 220 },
    // { field: 'consoChambre', headerName: 'Consommation par chambre (kWh)', type: 'number', width: 220 },
    {
        field: 'stars',
        headerName: 'Classification',
        type: 'number',
        width: 150,
        editable: false,
        sortable: true,
        renderCell: (params) => (
            <Rating
                value={params.value}
                readOnly
                size="small"
                color="grey"
                sx={{
                    '& .MuiRating-iconEmpty': {
                        visibility: 'hidden',
                    },
                    '& .MuiRating-iconFilled': {
                        color: 'primary.main',
                        opacity: 0.9,
                    },
                }}
            />
        ),
    },
    {
        field: 'completionRate',
        headerName: 'Data collection',
        width: 150,
        renderCell: (params) => {
            return <CustomProgressBar progress={params.value * 100} />
        },
    },
    { field: 'rooms', headerName: 'Nombre de chambres', type: 'number', width: 180, editable: true },
    { field: 'surface', headerName: 'Surface (m²)', type: 'number', width: 150, editable: true },
    // { field: 'occupation', headerName: 'Occupation (nuitées)', type: 'number', width: 200, editable: true },
    { field: 'travaux', headerName: 'Travaux (montant)', type: 'number', width: 180, editable: true },
    {
        field: 'totalConsumptionFinal',
        headerName: 'Consommation EF',
        valueFormatter: (params) => consumptionDataFormatter(params.value, 'kWh'),
        width: 200,
    },
    {
        field: 'totalConsumptionPrimary',
        headerName: 'Consommation EP',
        valueFormatter: (params) => consumptionDataFormatter(params.value, 'kWh'),
        width: 200,
    },
    {
        field: 'totalCO2',
        headerName: 'Émissions CO2',
        valueFormatter: (params) => consumptionDataFormatter(params.value, 'kg'),
        width: 200,
    },

    {
        field: 'dpe.emission.value',
        headerName: 'GES',
        width: 250,
        valueGetter: (params) => params.row.dpe.emission.value,
        renderCell: (params) => {
            const dpeLabel = params.row.dpe.emission
            return <DpeLabelWithValue dpe={dpeLabel} type="emission" />
        },
    },
]

const SortableDataGrid: FC<{ hotels: SiteData[] }> = ({ hotels }) => {
    const [pageSize, setPageSize] = useState(10)

    const getRowClassName = (params: any) => {
        return params.row.average ? 'super-app-theme--average' : ''
    }

    return (
        <Box
            sx={{
                width: '100%',
                background: 'white',
            }}
        >
            <StyledDataGrid
                autoHeight
                rows={hotels.map((h) => addMetrics(h))}
                columns={columns}
                pageSize={pageSize}
                rowsPerPageOptions={[10]}
                pagination
                disableSelectionOnClick
                getRowClassName={getRowClassName}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            />
        </Box>
    )
}

export default SortableDataGrid
